import { transparentize } from 'polished';
import { FONT_FAMILY } from '@utils/constants';

import { ANNOTATION_SERIES } from '@components/annotations/hooks/annotationOption';
import { METRIC_FILTER_SERIES } from './metricFilterSeriesOption';
import { DOD_SERIES } from './dodSeriesOption';
import { MANUAL_ALERT_SERIES } from '../hooks/alerts/manualAlertUtils';
import { AI_ALERT_MARKERS, AiAlertSeriesKeys } from '../hooks/alerts/constants';

export const TIME_AXIS = 'time-axis';
export const VALUE_AXIS = 'value-axis';

export const METRIC_SERIES = 'metric-series';

// echarts rendering breaks if the type of the placeholder charts is not specified
// weridly if there's a "bar" type among line charts without data then entire axis and render gets messed up

const SECONDARY_SERIES = [{
  id: METRIC_FILTER_SERIES,
  type: "line",
},{
  id: DOD_SERIES,
  type: "line",
}]

const MARKER_SERIES = [{
  id: AI_ALERT_MARKERS,
  type: "line",
}, {
  id: MANUAL_ALERT_SERIES,
  type: "line",
}, {
  id: ANNOTATION_SERIES,
  type: "line",
}];

const AI_ALERT_SERIES = AiAlertSeriesKeys.map( id => ({ id, type: 'line' }));

const axisColor = '#ccc';
const axisLabelColor = '#000';

const timeXAxis = {
  id: TIME_AXIS,
  type: 'time',
  show: true,
  splitNumber: 2,
  // boundaryGap: true,
  axisLabel: {
    color: axisLabelColor,
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    hideOverlap: true,
    showMinLabel: true,
    showMaxLabel: true,
  },
  splitLine: {
    show: false,
  },
  axisTick: {
    alignWithLabel: false,
    lineStyle: {
      color: axisColor,
    },
  },
  axisLine: {
    show: true,
    lineStyle: {
      color: axisColor,
    },
  },
};

/* eslint-disable import/prefer-default-export */
export const lineChartOptions = (metrics) => {
  const dataVarMetrics = metrics.map(metric => `data.results.${metric}`);
  const dataVar = [
    'data.timestamp',
    dataVarMetrics.join('|'),
  ];

  const aggregates = metrics.map(metric => `totals.${metric}`);
  const aggregateVar = aggregates.join('|');

  return {
    'out*data.results': [
      {
        id: '__seq__',
        xAxis: [
          timeXAxis,
          {...timeXAxis, id: 'dod-time-axis', show: false }
        ],
        yAxis: {
          id: VALUE_AXIS,
          type: 'value',
          show: true,
          splitNumber: 1,
          nameLocation: 'middle',
          axisLabel: {
            color: axisLabelColor,
            fontSize: 12,
            fontFamily: FONT_FAMILY,
            hideOverlap: true,
            showMinLabel: true,
            showMaxLabel: true,
          },
          splitLine: {
            show: true,
          },
          axisTick: {
            show: false,
          },
          axisLine: {
            show: false,
            lineStyle: {
              color: axisColor,
            },
          },
        },
        grid: {
          top: 30,
          bottom: 35,
          left: 50,
          right: 25,
        },
        series: [{
          id: METRIC_SERIES,
          'data*data.timestamp': [
            dataVar,
          ],
          type: 'line',
          symbol: 'circle',
          showSymbol: false,
          xAxisIndex: 0,
          itemStyle: {
            color: 'filterColor',
          },
          lineStyle: {
            width: 1.5,
          },
          areaStyle: {
            opacity: 0.2,
          },
          markLine: {},
          markArea: {},
          triggerLineEvent: true,
        },
        ...SECONDARY_SERIES,
        ...MARKER_SERIES,
        ...AI_ALERT_SERIES,
        ],
        tooltip: {
          trigger: 'axis',
          textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
          confine: true,
        },
        dataZoom: [{
          type: "slider",
          show: false,
          xAxisIndex: [0],
        }],
        toolbox: {
          show: false,
        },
        brush: {
          brushType: "lineX",
          seriesIndex: [0],
          brushLink: [0],
          xAxisIndex: [0],
          transformable: false,
          throttleType: 'debounce',
          throttleDelay: 300,
          brushStyle: {
            borderWidth: 1,
            color: 'rgba(120,140,180,0.3)',
            borderColor: 'rgb(224, 224, 224,0.8)'
          },
        },
        legend: {
          show: false,
          type: 'scroll',
        },
        animation: true,
        _metric: 'metric.name',
        _title: 'metric.displayName',
        _shortMetric: 'metric.short',
        _subscript: 'metric.subscript',
        _unit: 'metric.unit',
        _aggregate: aggregateVar,
        _deviation: 'totals.deviation',
        _viewType: 'timeseries',
      },
    ],
  };
};

// immutability update diff
export const getBarOptionDiff = ({ filterColor }: any) => {
  return {
    series: [
      {
        $merge: {
          type: 'bar',
          barCategoryGap: '1%',
          itemStyle: {
            color: transparentize(0.75, filterColor),
            borderColor: filterColor,
          },
        }
      }
    ]
  };
}


export const updateNaModeColor = ({ filterColor }: any) => {
  return {
    series: [
      {
        $merge: {
          lineStyle: {
            color: filterColor,
          }
        }
      }
    ]
  };
}
